import type { CurrentRecordModel } from '@records/state/current-record/current-record.model';
import { DataObject } from '@shared/models/data-object.model';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CURRENT_RECORD_STATE_TOKEN } from '../current-record/current-record-state.token';
import { ExecutableAction, ExecutableActionParam } from '../utils/executable-action';
import { CurrentDataModel } from './current-data.model';
import { processCompanyContacts } from './utils/preprocessor-company-contacts-factory';
import { processUsages } from './utils/process-usages';
import { EntityType } from '@shared/models/entity-enum';
import { leaseCompConfidentialityToBool } from '@shared/utils/helpers/lease-confidentiality';

export abstract class RecordFetcher {
  abstract fetch(...params: any): Observable<DataObject>;
}

export class GetRecord implements ExecutableAction<CurrentDataModel> {
  static readonly type = '[Current Data] Get Record';

  /**
   *
   * @param setRecordLoad set to false when there is a secondary process
   *
   * This action loads and separates the record into the state, in the case that the setRecordLoad flag is true,
   * the action will turn the recordLoaded flag to true when successfully gets the record, and,
   *  in the case where the setRecordLoad is false, the action will not turn the recordLoaded when finishing.
   */
  constructor(private recordFetcher: RecordFetcher) {}

  execute(params: ExecutableActionParam<CurrentDataModel>, useCacheResponse = true) {
    params.ctx.patchState({ recordLoaded: false });
    const currentRecordState: CurrentRecordModel = params.store.selectSnapshot(CURRENT_RECORD_STATE_TOKEN);
    const { type: entityType } = currentRecordState;
    return this.recordFetcher.fetch(useCacheResponse).pipe(
      map(record => {
        if (record) {
          const partialState = processUsages(record);
          record = processCompanyContacts(entityType, record);
          if (entityType === EntityType.Space) {
            record = leaseCompConfidentialityToBool(record);
          }
          const newState: any = {
            ...partialState,
            record,
            recordLoaded: true,
          };
          // Storing the Old record to compare it while updating the record
          newState.oldRecord = JSON.parse(JSON.stringify(record));
          params.ctx.setState(newState);
        } else {
          params.ctx.setState({
            record: null,
            usagesInfo: null,
            recordLoaded: false,
            oldRecord: null,
          });
        }
      }),
      catchError(err => {
        params.ctx.setState({
          record: null,
          usagesInfo: null,
          recordLoaded: false,
        });
        return throwError(err);
      })
    );
  }
}

export class GetRecordNoCache extends GetRecord {
  execute(params: ExecutableActionParam<CurrentDataModel>): Observable<void> {
    return super.execute(params, false);
  }
}
